/**
 * AppShell — layout wrapper for authenticated portal pages.
 * Responsive: sidebar collapses to a hamburger drawer on mobile.
 */
import { ReactNode, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Navbar from "./Navbar";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: "home" },
  { path: "/packages", label: "My Packages", icon: "package" },
  { path: "/shipments", label: "Shipments", icon: "truck" },
  { path: "/calculator", label: "Cost Calculator", icon: "calculator" },
  { path: "/invoices", label: "My Invoices", icon: "receipt" },
  { path: "/stores", label: "Store Integrations", icon: "store" },
  { path: "/address", label: "My US Address", icon: "mapPin" },
  { path: "/notifications", label: "Notifications", icon: "bell" },
];

const adminNavItems = [
  { path: "/admin", label: "Admin Dashboard", icon: "shield" },
  { path: "/admin/reports", label: "Reports", icon: "chart" },
  { path: "/admin/pricing", label: "Pricing Config", icon: "dollar" },
  { path: "/admin/settings", label: "Settings", icon: "cog" },
  { path: "/dashboard", label: "My Dashboard", icon: "home" },
  { path: "/packages", label: "My Packages", icon: "package" },
  { path: "/shipments", label: "Shipments", icon: "truck" },
  { path: "/calculator", label: "Cost Calculator", icon: "calculator" },
  { path: "/invoices", label: "My Invoices", icon: "receipt" },
  { path: "/address", label: "My US Address", icon: "mapPin" },
  { path: "/notifications", label: "Notifications", icon: "bell" },
];

function NavIcon({ name }: { name: string }) {
  const icons: Record<string, string> = {
    home: "M3 12l9-9 9 9M5 10v10h14V10",
    package: "M20 7l-8-4-8 4 8 4 8-4zM4 7v10l8 4 8-4V7",
    truck: "M3 3h13v13H3zM16 8h4l3 3v5h-7",
    store: "M4 13h16v8H4zM4 9l1-5h14l1 5",
    mapPin: "M12 22s8-7 8-12a8 8 0 10-16 0c0 5 8 12 8 12z",
    shield: "M12 2l9 4v6c0 5-4 9-9 10-5-1-9-5-9-10V4l9-4z",
    cog: "M12 8a4 4 0 100 8 4 4 0 000-8zM19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 11-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 11-4 0v-.09A1.65 1.65 0 008 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 11-2.83-2.83l.06-.06A1.65 1.65 0 004.6 15a1.65 1.65 0 00-1.51-1H3a2 2 0 110-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 112.83-2.83l.06.06A1.65 1.65 0 009 4.6a1.65 1.65 0 001-1.51V3a2 2 0 114 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 112.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 110 4h-.09a1.65 1.65 0 00-1.51 1z",
    calculator: "M4 2h16v20H4zM8 6h8M8 10h2M12 10h2M16 10h0M8 14h2M12 14h2M16 14h0M8 18h6",
    receipt: "M6 2h12v20l-3-2-3 2-3-2-3 2zM9 6h6M9 10h6M9 14h4",
    bell: "M15 17h5l-1.4-1.4A2 2 0 0118 14.2V11a6 6 0 10-12 0v3.2c0 .53-.21 1.04-.59 1.41L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9",
    chart: "M3 3v18h18M7 16l4-6 4 4 5-8",
    dollar: "M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6",
  };
  return (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" d={icons[name] || icons.home} />
    </svg>
  );
}

export default function AppShell({ children, admin = false }: { children: ReactNode; admin?: boolean }) {
  const location = useLocation();
  const items = admin ? adminNavItems : navItems;
  const [drawerOpen, setDrawerOpen] = useState(false);

  const SidebarContent = () => (
    <nav className="space-y-1">
      {items.map((item) => {
        const active = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={() => setDrawerOpen(false)}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              active
                ? "bg-[#FED7AA]/30 text-[#EA580C]"
                : "text-slate-600 hover:bg-slate-100"
            }`}
          >
            <NavIcon name={item.icon} />
            {item.label}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      {/* Mobile hamburger toggle bar */}
      <div className="md:hidden sticky top-16 z-30 bg-white border-b border-slate-200 px-4 py-2.5">
        <button
          onClick={() => setDrawerOpen(!drawerOpen)}
          className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-[#EA580C] transition-colors"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            {drawerOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />}
          </svg>
          {drawerOpen ? "Close Menu" : "Menu"}
        </button>
      </div>

      {/* Mobile drawer overlay */}
      {drawerOpen && (
        <div
          className="md:hidden fixed inset-0 top-28 z-30 bg-slate-900/30"
          onClick={() => setDrawerOpen(false)}
        />
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex gap-6">
        {/* Desktop sidebar */}
        <aside className="hidden md:block w-56 flex-shrink-0">
          <SidebarContent />
        </aside>

        {/* Mobile drawer */}
        {drawerOpen && (
          <aside className="md:hidden fixed left-0 top-28 bottom-0 w-64 bg-white border-r border-slate-200 z-40 overflow-y-auto px-4 py-4 shadow-lg">
            <SidebarContent />
          </aside>
        )}

        {/* Main content */}
        <main className="flex-1 min-w-0">{children}</main>
      </div>
    </div>
  );
}